import React, { useState, useMemo } from 'react';
import MainLayout from '../../components/ui/MainLayout';
import Icon from '../../components/AppIcon';
import { useLanguage } from '../../contexts/LanguageContext';
import { useBooking } from '../../contexts/BookingContext';
import { useRealtime } from '../../contexts/RealtimeContext';
import * as XLSX from 'xlsx';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const BUILDINGS = ['All', 'F1', 'F2', 'G1', 'G2', 'H', 'L'];

const MONTHLY_DATA = [];

const BUILDING_STATS = [];

const STATUS_DIST = [];

const REPORT_TYPES = [
  { id: 'occupancy', labelEn: 'Occupancy Summary', labelFr: 'Résumé d\'occupation', icon: 'BarChart3' },
  { id: 'monthly', labelEn: 'Monthly Trends', labelFr: 'Tendances mensuelles', icon: 'TrendingUp' },
  { id: 'building', labelEn: 'Building Breakdown', labelFr: 'Détail par bâtiment', icon: 'Building2' },
  { id: 'checkin', labelEn: 'Check-in / Check-out Log', labelFr: 'Journal entrées/sorties', icon: 'ClipboardCheck' },
];

const CHECKIN_LOG = [];

function downloadCSV(data, filename) {
  if (!data?.length) return;
  const headers = Object.keys(data?.[0]);
  const rows = data?.map(row => headers?.map(h => `"${row?.[h] ?? ''}"`)?.join(','));
  const csv = [headers?.join(','), ...rows]?.join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a?.click();
  URL.revokeObjectURL(url);
}

function downloadPDF(reportType, building, language) {
  const title = language === 'fr' ? 'Rapport d\'occupation — US Réservation Résidences' : 'Occupancy Report — US Réservation Résidences';
  const date = new Date()?.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const buildingLabel = building === 'All' ? (language === 'fr' ? 'Tous les bâtiments' : 'All Buildings') : `${language === 'fr' ? 'Bâtiment' : 'Building'} ${building}`;

  const totalRooms = 0;
  const occupied = 0;
  const available = 0;
  const rate = '0.0';

  const htmlContent = `<!DOCTYPE html>
<html lang="${language}">
<head>
<meta charset="UTF-8"/>
<title>${title}</title>
<style>
  body { font-family: Arial, sans-serif; margin: 40px; color: #1a1a2e; }
  h1 { color: #1B2D5B; font-size: 22px; border-bottom: 3px solid #B8963E; padding-bottom: 10px; }
  h2 { color: #1B2D5B; font-size: 16px; margin-top: 28px; }
  .meta { color: #666; font-size: 13px; margin-bottom: 24px; }
  table { width: 100%; border-collapse: collapse; margin-top: 12px; font-size: 13px; }
  th { background: #1B2D5B; color: white; padding: 10px 12px; text-align: left; }
  td { padding: 9px 12px; border-bottom: 1px solid #e5e7eb; }
  tr:nth-child(even) td { background: #f8f9fa; }
  .stat-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin: 20px 0; }
  .stat-box { background: #f0f4ff; border-left: 4px solid #1B2D5B; padding: 14px 16px; border-radius: 6px; }
  .stat-val { font-size: 26px; font-weight: bold; color: #1B2D5B; }
  .stat-lbl { font-size: 12px; color: #666; margin-top: 4px; }
  .footer { margin-top: 40px; font-size: 11px; color: #999; border-top: 1px solid #e5e7eb; padding-top: 12px; }
</style>
</head>
<body>
<h1>${title}</h1>
<div class="meta">${language === 'fr' ? 'Généré le' : 'Generated on'}: ${date} &nbsp;|&nbsp; ${buildingLabel}</div>

<h2>${language === 'fr' ? 'Résumé de l\'occupation' : 'Occupancy Summary'}</h2>
<div class="stat-grid">
  <div class="stat-box"><div class="stat-val">${totalRooms}</div><div class="stat-lbl">${language === 'fr' ? 'Chambres totales' : 'Total Rooms'}</div></div>
  <div class="stat-box"><div class="stat-val">${occupied}</div><div class="stat-lbl">${language === 'fr' ? 'Occupées' : 'Occupied'}</div></div>
  <div class="stat-box"><div class="stat-val">${available}</div><div class="stat-lbl">${language === 'fr' ? 'Disponibles' : 'Available'}</div></div>
  <div class="stat-box"><div class="stat-val">${rate}%</div><div class="stat-lbl">${language === 'fr' ? 'Taux d\'occupation' : 'Occupancy Rate'}</div></div>
</div>

<h2>${language === 'fr' ? 'Détail par bâtiment' : 'Building Breakdown'}</h2>
<table>
  <thead><tr>
    <th>${language === 'fr' ? 'Bâtiment' : 'Building'}</th>
    <th>${language === 'fr' ? 'Total' : 'Total'}</th>
    <th>${language === 'fr' ? 'Occupées' : 'Occupied'}</th>
    <th>${language === 'fr' ? 'Disponibles' : 'Available'}</th>
    <th>${language === 'fr' ? 'Taux' : 'Rate'}</th>
  </tr></thead>
  <tbody>
    ${BUILDING_STATS?.map(b => `<tr><td>${b?.building}</td><td>${b?.total}</td><td>${b?.occupied}</td><td>${b?.available}</td><td>${b?.rate}%</td></tr>`)?.join('')}
  </tbody>
</table>

<h2>${language === 'fr' ? 'Tendances mensuelles (2026)' : 'Monthly Trends (2026)'}</h2>
<table>
  <thead><tr>
    <th>${language === 'fr' ? 'Mois' : 'Month'}</th>
    <th>${language === 'fr' ? 'Taux d\'occupation (%)' : 'Occupancy Rate (%)'}</th>
    <th>${language === 'fr' ? 'Entrées' : 'Check-ins'}</th>
    <th>${language === 'fr' ? 'Sorties' : 'Check-outs'}</th>
    <th>${language === 'fr' ? 'Disponibles' : 'Available'}</th>
  </tr></thead>
  <tbody>
    ${MONTHLY_DATA?.map(m => `<tr><td>${m?.month}</td><td>${m?.occupancy}%</td><td>${m?.checkIns}</td><td>${m?.checkOuts}</td><td>${m?.available}</td></tr>`)?.join('')}
  </tbody>
</table>

<div class="footer">Université Senghor — US Réservation Résidences &nbsp;|&nbsp; ${language === 'fr' ? 'Rapport confidentiel' : 'Confidential Report'}</div>
</body>
</html>`;

  const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `occupancy-report-${new Date()?.toISOString()?.slice(0, 10)}.html`;
  a?.click();
  URL.revokeObjectURL(url);
}

// ── Check-in/Check-out Log export helpers ────────────────────────────────────

function buildLogExportRows(filteredLog, logStats, language) {
  const ts = new Date()?.toLocaleString(language === 'fr' ? 'fr-FR' : 'en-US');
  const summaryHeader = language === 'fr' ? '=== STATISTIQUES ===' : '=== SUMMARY STATISTICS ===';
  const dataHeader = language === 'fr' ? '=== JOURNAL ===' : '=== LOG ENTRIES ===';

  const summaryRows = [
    { [language === 'fr' ? 'Métrique' : 'Metric']: language === 'fr' ? 'Généré le' : 'Generated At', [language === 'fr' ? 'Valeur' : 'Value']: ts },
    { [language === 'fr' ? 'Métrique' : 'Metric']: summaryHeader, [language === 'fr' ? 'Valeur' : 'Value']: '' },
    { [language === 'fr' ? 'Métrique' : 'Metric']: language === 'fr' ? 'Résidents totaux' : 'Total Residents', [language === 'fr' ? 'Valeur' : 'Value']: logStats?.totalResidents },
    { [language === 'fr' ? 'Métrique' : 'Metric']: language === 'fr' ? 'Total entrées' : 'Total Check-ins', [language === 'fr' ? 'Valeur' : 'Value']: logStats?.totalCheckIns },
    { [language === 'fr' ? 'Métrique' : 'Metric']: language === 'fr' ? 'Total sorties' : 'Total Check-outs', [language === 'fr' ? 'Valeur' : 'Value']: logStats?.totalCheckOuts },
    { [language === 'fr' ? 'Métrique' : 'Metric']: language === 'fr' ? 'Durée moy. séjour' : 'Avg. Length of Stay', [language === 'fr' ? 'Valeur' : 'Value']: logStats?.avgLengthOfStay !== '—' ? `${logStats?.avgLengthOfStay} ${language === 'fr' ? 'jours' : 'days'}` : '—' },
    { [language === 'fr' ? 'Métrique' : 'Metric']: language === 'fr' ? "Taux d'occupation" : 'Occupancy Rate', [language === 'fr' ? 'Valeur' : 'Value']: `${logStats?.periodOccupancyRate}%` },
  ];

  const logRows = filteredLog?.map(r => ({
    [language === 'fr' ? 'Résident' : 'Resident']: r?.residentName || '—',
    [language === 'fr' ? 'Chambre' : 'Room']: r?.room || '—',
    [language === 'fr' ? 'Bâtiment' : 'Building']: r?.building || '—',
    [language === 'fr' ? 'Entrée' : 'Check-in']: r?.checkIn || '—',
    [language === 'fr' ? 'Sortie' : 'Check-out']: r?.checkOut || '—',
    [language === 'fr' ? 'Durée (jours)' : 'Length (days)']: r?.lengthOfStay != null ? r?.lengthOfStay : '—',
    [language === 'fr' ? 'Statut' : 'Status']: r?.status || '—',
  }));

  return { summaryRows, logRows, dataHeader, ts };
}

function exportLogCSV(filteredLog, logStats, language, logDateFrom, logDateTo) {
  const { summaryRows, logRows, dataHeader } = buildLogExportRows(filteredLog, logStats, language);
  const metricKey = language === 'fr' ? 'Métrique' : 'Metric';
  const valueKey = language === 'fr' ? 'Valeur' : 'Value';
  const logKeys = logRows?.length > 0 ? Object.keys(logRows?.[0]) : [];

  const lines = [];
  // Summary block
  summaryRows?.forEach(r => lines?.push(`"${r?.[metricKey]}","${r?.[valueKey]}"`));
  lines?.push('');
  // Data header
  lines?.push(`"${dataHeader}"`);
  // Log table header
  if (logKeys?.length) lines?.push(logKeys?.map(k => `"${k}"`)?.join(','));
  // Log rows
  logRows?.forEach(r => lines?.push(logKeys?.map(k => `"${r?.[k] ?? ''}"`)?.join(',')));

  const csv = lines?.join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `checkin-log-${logDateFrom}-${logDateTo}.csv`;
  a?.click();
  URL.revokeObjectURL(url);
}

function exportLogExcel(filteredLog, logStats, language, logDateFrom, logDateTo) {
  const { summaryRows, logRows, dataHeader, ts } = buildLogExportRows(filteredLog, logStats, language);
  const wb = XLSX?.utils?.book_new();

  // Summary sheet
  const summaryData = [
    [language === 'fr' ? 'Métrique' : 'Metric', language === 'fr' ? 'Valeur' : 'Value'],
    ...summaryRows?.map(r => [r?.[language === 'fr' ? 'Métrique' : 'Metric'], r?.[language === 'fr' ? 'Valeur' : 'Value']]),
  ];
  const summarySheet = XLSX?.utils?.aoa_to_sheet(summaryData);
  summarySheet['!cols'] = [{ wch: 30 }, { wch: 25 }];
  XLSX?.utils?.book_append_sheet(wb, summarySheet, language === 'fr' ? 'Statistiques' : 'Summary');

  // Log sheet
  const logSheet = XLSX?.utils?.json_to_sheet(logRows);
  logSheet['!cols'] = [{ wch: 25 }, { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }];
  XLSX?.utils?.book_append_sheet(wb, logSheet, language === 'fr' ? 'Journal' : 'Log');

  XLSX?.writeFile(wb, `checkin-log-${logDateFrom}-${logDateTo}.xlsx`);
}

function exportLogPDF(filteredLog, logStats, language, logDateFrom, logDateTo) {
  const ts = new Date()?.toLocaleString(language === 'fr' ? 'fr-FR' : 'en-US');
  const title = language === 'fr' ? 'Journal Entrées / Sorties' : 'Check-in / Check-out Log';
  const subtitle = 'Université Senghor — US Réservation Résidences';
  const periodLabel = language === 'fr' ? 'Période' : 'Period';
  const generatedLabel = language === 'fr' ? 'Généré le' : 'Generated at';

  const statsRows = [
    [language === 'fr' ? 'Résidents totaux' : 'Total Residents', logStats?.totalResidents],
    [language === 'fr' ? 'Total entrées' : 'Total Check-ins', logStats?.totalCheckIns],
    [language === 'fr' ? 'Total sorties' : 'Total Check-outs', logStats?.totalCheckOuts],
    [language === 'fr' ? 'Durée moy. séjour' : 'Avg. Length of Stay', logStats?.avgLengthOfStay !== '—' ? `${logStats?.avgLengthOfStay} ${language === 'fr' ? 'jours' : 'days'}` : '—'],
    [language === 'fr' ? "Taux d'occupation" : 'Occupancy Rate', `${logStats?.periodOccupancyRate}%`],
  ];

  const logTableRows = filteredLog?.map(r => [
    r?.residentName || '—',
    r?.room || '—',
    r?.building || '—',
    r?.checkIn || '—',
    r?.checkOut || '—',
    r?.lengthOfStay != null ? `${r?.lengthOfStay} ${language === 'fr' ? 'j' : 'd'}` : '—',
    r?.status || '—',
  ]);

  const logTableHeaders = language === 'fr'
    ? ['Résident', 'Chambre', 'Bâtiment', 'Entrée', 'Sortie', 'Durée', 'Statut']
    : ['Resident', 'Room', 'Building', 'Check-in', 'Check-out', 'Length', 'Status'];

  const statsTableRows = statsRows?.map(([m, v]) => `<tr><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;font-weight:600;color:#374151;">${m}</td><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;font-weight:700;color:#1B2D5B;">${v}</td></tr>`)?.join('');
  const logTrRows = logTableRows?.map((cols, i) => `<tr style="background:${i % 2 === 0 ? '#fff' : '#f9fafb'};">${cols?.map(c => `<td style="padding:7px 10px;border-bottom:1px solid #e5e7eb;font-size:12px;color:#374151;">${c}</td>`)?.join('')}</tr>`)?.join('');
  const logThCols = logTableHeaders?.map(h => `<th style="padding:9px 10px;background:#1B2D5B;color:#fff;text-align:left;font-size:12px;">${h}</th>`)?.join('');

  const html = `<!DOCTYPE html>
<html lang="${language}">
<head>
<meta charset="UTF-8"/>
<title>${title}</title>
<style>
  body { font-family: Arial, sans-serif; margin: 36px; color: #1a1a2e; }
  h1 { color: #1B2D5B; font-size: 20px; border-bottom: 3px solid #B8963E; padding-bottom: 8px; margin-bottom: 4px; }
  .sub { color: #6b7280; font-size: 13px; margin-bottom: 6px; }
  .meta { color: #9ca3af; font-size: 12px; margin-bottom: 24px; }
  h2 { color: #1B2D5B; font-size: 14px; margin-top: 28px; margin-bottom: 10px; }
  table { width: 100%; border-collapse: collapse; }
  .footer { margin-top: 40px; font-size: 11px; color: #9ca3af; border-top: 1px solid #e5e7eb; padding-top: 10px; }
</style>
</head>
<body>
<h1>${title}</h1>
<div class="sub">${subtitle}</div>
<div class="meta">${periodLabel}: ${logDateFrom} → ${logDateTo} &nbsp;|&nbsp; ${generatedLabel}: ${ts}</div>

<h2>${language === 'fr' ? 'Statistiques de la période' : 'Period Summary Statistics'}</h2>
<table style="max-width:420px;">
  <tbody>${statsTableRows}</tbody>
</table>

<h2>${language === 'fr' ? 'Détail du journal' : 'Log Entries'} (${filteredLog?.length})</h2>
<table>
  <thead><tr>${logThCols}</tr></thead>
  <tbody>${logTrRows}</tbody>
</table>

<div class="footer">Université Senghor — Résidences &nbsp;|&nbsp; ${language === 'fr' ? 'Document confidentiel' : 'Confidential Document'}</div>
</body>
</html>`;

  const blob = new Blob([html], { type: 'text/html;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `checkin-log-${logDateFrom}-${logDateTo}.pdf.html`;
  a?.click();
  URL.revokeObjectURL(url);
}

const AdvancedReporting = () => {
  const { t, language } = useLanguage();
  const { rooms } = useBooking();
  const realtime = useRealtime();

  // Use Supabase-backed stays only — normalize field names
  const stays = React.useMemo(() => {
    return (realtime?.stays || [])?.map(s => ({
      id: s?.id,
      residentName: s?.resident_name || s?.residentName || '',
      room: s?.room_number || s?.room || '',
      building: s?.building || '',
      checkIn: s?.check_in_date || s?.checkIn || '',
      checkOut: s?.check_out_date || s?.checkOut || '',
      status: s?.status || '',
      guestStatus: s?.guest_status || s?.guestStatus || '',
      accommodationType: s?.accommodation_type || s?.accommodationType || '',
    }));
  }, [realtime?.stays]);

  const [selectedBuilding, setSelectedBuilding] = useState('All');
  const [selectedReport, setSelectedReport] = useState('occupancy');
  const [dateFrom, setDateFrom] = useState('2026-01-01');
  const [dateTo, setDateTo] = useState('2026-12-31');

  // Check-in/Check-out Log specific filters
  const [logDateFrom, setLogDateFrom] = useState('2026-01-01');
  const [logDateTo, setLogDateTo] = useState('2026-12-31');
  const [logRoomFilter, setLogRoomFilter] = useState('');
  const [logGuestFilter, setLogGuestFilter] = useState('');
  const [logDeptFilter, setLogDeptFilter] = useState('All');

  const totalRooms = 0;
  const occupied = 0;
  const available = 0;
  const maintenance = 0;
  const reserved = 0;
  const occupancyRate = '0.0';

  const filteredBuildingStats = useMemo(() => {
    if (selectedBuilding === 'All') return BUILDING_STATS;
    return BUILDING_STATS?.filter(b => b?.building === selectedBuilding);
  }, [selectedBuilding]);

  // ── Build full stay log from BookingContext ──
  const stayLog = useMemo(() => {
    return stays?.map(s => {
      const checkInDate = s?.checkIn || '';
      const checkOutDate = s?.checkOut || '';
      const msPerDay = 1000 * 60 * 60 * 24;
      const lengthOfStay = checkInDate && checkOutDate
        ? Math.max(1, Math.round((new Date(checkOutDate) - new Date(checkInDate)) / msPerDay))
        : null;
      return {
        id: s?.id,
        residentName: s?.residentName || '',
        room: s?.room || '',
        building: s?.building || '',
        department: s?.building || '',
        checkIn: checkInDate,
        checkOut: checkOutDate,
        status: s?.status || '',
        guestStatus: s?.guestStatus || '',
        lengthOfStay,
        accommodationType: s?.accommodationType || '',
      };
    });
  }, [stays]);

  // ── Filtered log ──
  const filteredLog = useMemo(() => {
    return stayLog?.filter(row => {
      const inDateRange = (!logDateFrom || row?.checkIn >= logDateFrom) &&
        (!logDateTo || row?.checkIn <= logDateTo || row?.checkOut <= logDateTo || row?.checkOut >= logDateFrom);
      const inRoom = !logRoomFilter || row?.room?.toLowerCase()?.includes(logRoomFilter?.toLowerCase());
      const inGuest = !logGuestFilter || row?.residentName?.toLowerCase()?.includes(logGuestFilter?.toLowerCase());
      const inDept = logDeptFilter === 'All' || row?.building === logDeptFilter;
      return inDateRange && inRoom && inGuest && inDept;
    });
  }, [stayLog, logDateFrom, logDateTo, logRoomFilter, logGuestFilter, logDeptFilter]);

  // ── Summary statistics for the log ──
  const logStats = useMemo(() => {
    const totalResidents = new Set(filteredLog?.map(r => r?.residentName))?.size;
    const totalCheckIns = filteredLog?.filter(r =>
      r?.guestStatus === 'checked-in' || r?.status === 'confirmed-stay' || r?.status === 'active'
    )?.length;
    const totalCheckOuts = filteredLog?.filter(r =>
      r?.status === 'completed-stay' || r?.status === 'checked-out' || r?.guestStatus === 'checked-out'
    )?.length;
    const staysWithLength = filteredLog?.filter(r => r?.lengthOfStay != null);
    const avgLengthOfStay = staysWithLength?.length
      ? (staysWithLength?.reduce((sum, r) => sum + (r?.lengthOfStay || 0), 0) / staysWithLength?.length)?.toFixed(1)
      : '—';
    const occupiedInPeriod = filteredLog?.filter(r =>
      r?.status === 'confirmed-stay' || r?.status === 'active-reservation' || r?.status === 'active' || r?.status === 'pending'
    )?.length;
    const periodOccupancyRate = filteredLog?.length > 0
      ? ((occupiedInPeriod / totalRooms) * 100)?.toFixed(1)
      : '0.0';
    return { totalResidents, totalCheckIns, totalCheckOuts, avgLengthOfStay, periodOccupancyRate };
  }, [filteredLog]);

  const handleExportCSV = () => {
    if (selectedReport === 'monthly') {
      downloadCSV(MONTHLY_DATA, `monthly-trends-${dateFrom}-${dateTo}.csv`);
    } else if (selectedReport === 'building') {
      downloadCSV(filteredBuildingStats, `building-stats-${selectedBuilding}.csv`);
    } else if (selectedReport === 'checkin') {
      const exportData = filteredLog?.map(r => ({
        resident: r?.residentName,
        room: r?.room,
        building: r?.building,
        checkIn: r?.checkIn,
        checkOut: r?.checkOut,
        status: r?.status,
        lengthOfStay: r?.lengthOfStay != null ? `${r?.lengthOfStay} days` : '—',
      }));
      downloadCSV(exportData, `checkin-log-${logDateFrom}-${logDateTo}.csv`);
    } else {
      const summary = [
        { metric: language === 'fr' ? 'Total chambres' : 'Total Rooms', value: totalRooms },
        { metric: language === 'fr' ? 'Occupées' : 'Occupied', value: occupied },
        { metric: language === 'fr' ? 'Disponibles' : 'Available', value: available },
        { metric: language === 'fr' ? 'Maintenance' : 'Maintenance', value: maintenance },
        { metric: language === 'fr' ? 'Réservées' : 'Reserved', value: reserved },
        { metric: language === 'fr' ? 'Taux d\'occupation' : 'Occupancy Rate', value: `${occupancyRate}%` },
      ];
      downloadCSV(summary, `occupancy-summary-${new Date()?.toISOString()?.slice(0, 10)}.csv`);
    }
  };

  const handleExportPDF = () => {
    downloadPDF(selectedReport, selectedBuilding, language);
  };

  const handleExportLogCSV = () => exportLogCSV(filteredLog, logStats, language, logDateFrom, logDateTo);
  const handleExportLogExcel = () => exportLogExcel(filteredLog, logStats, language, logDateFrom, logDateTo);
  const handleExportLogPDF = () => exportLogPDF(filteredLog, logStats, language, logDateFrom, logDateTo);

  const labels = {
    title: language === 'fr' ? 'Rapports avancés' : 'Advanced Reports',
    subtitle: language === 'fr' ? 'Statistiques d\'occupation exportables' : 'Exportable occupancy statistics',
    reportType: language === 'fr' ? 'Type de rapport' : 'Report Type',
    building: language === 'fr' ? 'Bâtiment' : 'Building',
    dateRange: language === 'fr' ? 'Période' : 'Date Range',
    exportCSV: language === 'fr' ? 'Exporter CSV' : 'Export CSV',
    exportPDF: language === 'fr' ? 'Exporter PDF' : 'Export PDF',
    exportLogCSV: language === 'fr' ? 'CSV' : 'CSV',
    exportLogExcel: language === 'fr' ? 'Excel' : 'Excel',
    exportLogPDF: language === 'fr' ? 'PDF' : 'PDF',
    totalRooms: language === 'fr' ? 'Chambres totales' : 'Total Rooms',
    occupied: language === 'fr' ? 'Occupées' : 'Occupied',
    available: language === 'fr' ? 'Disponibles' : 'Available',
    occupancyRate: language === 'fr' ? 'Taux d\'occupation' : 'Occupancy Rate',
    monthlyTrends: language === 'fr' ? 'Tendances mensuelles' : 'Monthly Trends',
    buildingBreakdown: language === 'fr' ? 'Détail par bâtiment' : 'Building Breakdown',
    statusDistribution: language === 'fr' ? 'Distribution des statuts' : 'Status Distribution',
    checkinLog: language === 'fr' ? 'Journal entrées/sorties' : 'Check-in / Check-out Log',
    resident: language === 'fr' ? 'Résident' : 'Resident',
    room: language === 'fr' ? 'Chambre' : 'Room',
    type: language === 'fr' ? 'Type' : 'Type',
    date: language === 'fr' ? 'Date' : 'Date',
    rate: language === 'fr' ? 'Taux' : 'Rate',
    total: language === 'fr' ? 'Total' : 'Total',
    from: language === 'fr' ? 'Du' : 'From',
    to: language === 'fr' ? 'Au' : 'To',
    guestName: language === 'fr' ? 'Nom du résident' : 'Guest Name',
    roomNumber: language === 'fr' ? 'N° chambre' : 'Room Number',
    department: language === 'fr' ? 'Département' : 'Department',
    checkIn: language === 'fr' ? 'Entrée' : 'Check-in',
    checkOut: language === 'fr' ? 'Sortie' : 'Check-out',
    lengthOfStay: language === 'fr' ? 'Durée (jours)' : 'Length (days)',
    status: language === 'fr' ? 'Statut' : 'Status',
    totalResidents: language === 'fr' ? 'Résidents totaux' : 'Total Residents',
    totalCheckIns: language === 'fr' ? 'Total entrées' : 'Total Check-ins',
    totalCheckOuts: language === 'fr' ? 'Total sorties' : 'Total Check-outs',
    avgLengthOfStay: language === 'fr' ? 'Durée moy. séjour' : 'Avg. Length of Stay',
    periodOccupancyRate: language === 'fr' ? 'Taux d\'occupation' : 'Occupancy Rate',
    days: language === 'fr' ? 'jours' : 'days',
    searchGuest: language === 'fr' ? 'Rechercher un résident…' : 'Search guest…',
    searchRoom: language === 'fr' ? 'Ex: F1-201' : 'e.g. F1-201',
    allDepts: language === 'fr' ? 'Tous les bâtiments' : 'All Buildings',
    noResults: language === 'fr' ? 'Aucun résultat trouvé' : 'No results found',
    clearFilters: language === 'fr' ? 'Effacer les filtres' : 'Clear Filters',
    entries: language === 'fr' ? 'entrées' : 'entries',
  };

  const getStatusBadge = (row) => {
    if (row?.status === 'completed-stay' || row?.status === 'checked-out' || row?.guestStatus === 'checked-out') {
      return {
        label: language === 'fr' ? 'Séjour terminé' : 'Completed Stay',
        icon: 'LogOut',
        cls: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/30 dark:text-rose-400 dark:border-rose-800',
      };
    }
    if (row?.status === 'confirmed-stay' || row?.status === 'active' || row?.guestStatus === 'checked-in') {
      return {
        label: language === 'fr' ? 'Séjour confirmé' : 'Confirmed Stay',
        icon: 'UserCheck',
        cls: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-400 dark:border-emerald-800',
      };
    }
    if (row?.status === 'active-reservation' || row?.status === 'pending') {
      return {
        label: language === 'fr' ? 'Réservation active' : 'Active Reservation',
        icon: 'CalendarCheck',
        cls: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-400 dark:border-blue-800',
      };
    }
    return {
      label: language === 'fr' ? 'En attente' : 'Pending',
      icon: 'Clock',
      cls: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-800',
    };
  };

  const hasLogFilters = logRoomFilter || logGuestFilter || logDeptFilter !== 'All' || logDateFrom !== '2026-01-01' || logDateTo !== '2026-12-31';

  return (
    <MainLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {labels?.title}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">{labels?.subtitle}</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleExportCSV}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border bg-card text-foreground text-sm font-medium hover:bg-accent transition-colors"
            >
              <Icon name="FileSpreadsheet" size={16} />
              {labels?.exportCSV}
            </button>
            <button
              onClick={handleExportPDF}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors"
              style={{ background: '#1B2D5B' }}
            >
              <Icon name="FileText" size={16} />
              {labels?.exportPDF}
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-card border border-border rounded-xl p-4 flex flex-wrap gap-4 items-end">
          <div className="flex flex-col gap-1 min-w-[160px]">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{labels?.reportType}</label>
            <select
              value={selectedReport}
              onChange={e => setSelectedReport(e?.target?.value)}
              className="border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2"
              style={{ '--tw-ring-color': '#1B2D5B' }}
            >
              {REPORT_TYPES?.map(r => (
                <option key={r?.id} value={r?.id}>{language === 'fr' ? r?.labelFr : r?.labelEn}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1 min-w-[120px]">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{labels?.building}</label>
            <select
              value={selectedBuilding}
              onChange={e => setSelectedBuilding(e?.target?.value)}
              className="border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none"
            >
              {BUILDINGS?.map(b => (
                <option key={b} value={b}>{b === 'All' ? (language === 'fr' ? 'Tous' : 'All') : b}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{labels?.from}</label>
            <input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e?.target?.value)}
              className="border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{labels?.to}</label>
            <input
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e?.target?.value)}
              className="border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none"
            />
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: labels?.totalRooms, value: totalRooms, icon: 'DoorOpen', color: '#1B2D5B', bg: 'bg-blue-50 dark:bg-blue-950/30' },
            { label: labels?.occupied, value: occupied, icon: 'Users', color: '#B8963E', bg: 'bg-amber-50 dark:bg-amber-950/30' },
            { label: labels?.available, value: available, icon: 'CheckCircle', color: '#22c55e', bg: 'bg-green-50 dark:bg-green-950/30' },
            { label: labels?.occupancyRate, value: `${occupancyRate}%`, icon: 'BarChart3', color: '#6366f1', bg: 'bg-indigo-50 dark:bg-indigo-950/30' },
          ]?.map((card, i) => (
            <div key={i} className={`${card?.bg} border border-border rounded-xl p-4 flex items-center gap-3`}>
              <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${card?.color}20` }}>
                <Icon name={card?.icon} size={20} style={{ color: card?.color }} />
              </div>
              <div>
                <div className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>{card?.value}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{card?.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Monthly Trend */}
          <div className="lg:col-span-2 bg-card border border-border rounded-xl p-5">
            <h2 className="text-base font-semibold text-foreground mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {labels?.monthlyTrends}
            </h2>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={MONTHLY_DATA} margin={{ top: 4, right: 16, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="occupancy" stroke="#1B2D5B" strokeWidth={2} dot={false} name={language === 'fr' ? 'Occupation %' : 'Occupancy %'} />
                <Line type="monotone" dataKey="checkIns" stroke="#B8963E" strokeWidth={2} dot={false} name={language === 'fr' ? 'Entrées' : 'Check-ins'} />
                <Line type="monotone" dataKey="checkOuts" stroke="#22c55e" strokeWidth={2} dot={false} name={language === 'fr' ? 'Sorties' : 'Check-outs'} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Status Distribution */}
          <div className="bg-card border border-border rounded-xl p-5">
            <h2 className="text-base font-semibold text-foreground mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {labels?.statusDistribution}
            </h2>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={STATUS_DIST} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value">
                  {STATUS_DIST?.map((entry, index) => (
                    <Cell key={index} fill={entry?.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-1.5 mt-2">
              {STATUS_DIST?.map((s, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ background: s?.color }} />
                    <span className="text-muted-foreground">{s?.name}</span>
                  </div>
                  <span className="font-semibold text-foreground">{s?.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Building Breakdown Bar Chart */}
        <div className="bg-card border border-border rounded-xl p-5">
          <h2 className="text-base font-semibold text-foreground mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {labels?.buildingBreakdown}
          </h2>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={filteredBuildingStats} margin={{ top: 4, right: 16, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="building" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="occupied" fill="#1B2D5B" name={language === 'fr' ? 'Occupées' : 'Occupied'} radius={[4, 4, 0, 0]} />
              <Bar dataKey="available" fill="#B8963E" name={language === 'fr' ? 'Disponibles' : 'Available'} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* ══════════════════════════════════════════════════════════════════
            CHECK-IN / CHECK-OUT LOG SECTION
        ══════════════════════════════════════════════════════════════════ */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          {/* Section Header */}
          <div className="px-5 py-4 border-b border-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2" style={{ background: 'linear-gradient(135deg, #1B2D5B08 0%, #B8963E08 100%)' }}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: '#1B2D5B15' }}>
                <Icon name="ClipboardCheck" size={18} style={{ color: '#1B2D5B' }} />
              </div>
              <div>
                <h2 className="text-base font-semibold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {labels?.checkinLog}
                </h2>
                <p className="text-xs text-muted-foreground">
                  {filteredLog?.length} {labels?.entries}
                  {hasLogFilters && <span className="ml-2 text-amber-600 font-medium">· {language === 'fr' ? 'Filtres actifs' : 'Filters active'}</span>}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Export Buttons */}
              <button
                onClick={handleExportLogCSV}
                disabled={filteredLog?.length === 0}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed dark:bg-emerald-950/30 dark:text-emerald-400 dark:border-emerald-800 dark:hover:bg-emerald-900/40"
                title={language === 'fr' ? 'Exporter en CSV' : 'Export as CSV'}
              >
                <Icon name="FileSpreadsheet" size={13} />
                {labels?.exportLogCSV}
              </button>
              <button
                onClick={handleExportLogExcel}
                disabled={filteredLog?.length === 0}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed dark:bg-blue-950/30 dark:text-blue-400 dark:border-blue-800 dark:hover:bg-blue-900/40"
                title={language === 'fr' ? 'Exporter en Excel' : 'Export as Excel'}
              >
                <Icon name="Table" size={13} />
                {labels?.exportLogExcel}
              </button>
              <button
                onClick={handleExportLogPDF}
                disabled={filteredLog?.length === 0}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: '#1B2D5B' }}
                title={language === 'fr' ? 'Exporter en PDF' : 'Export as PDF'}
              >
                <Icon name="FileText" size={13} />
                {labels?.exportLogPDF}
              </button>
              {hasLogFilters && (
                <button
                  onClick={() => { setLogRoomFilter(''); setLogGuestFilter(''); setLogDeptFilter('All'); setLogDateFrom('2026-01-01'); setLogDateTo('2026-12-31'); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-border bg-background text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                >
                  <Icon name="X" size={12} />
                  {labels?.clearFilters}
                </button>
              )}
            </div>
          </div>

          {/* Log Filters */}
          <div className="px-5 py-4 border-b border-border bg-muted/20">
            <div className="flex flex-wrap gap-3 items-end">
              {/* Date From */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{labels?.from}</label>
                <input
                  type="date"
                  value={logDateFrom}
                  onChange={e => setLogDateFrom(e?.target?.value)}
                  className="border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>
              {/* Date To */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{labels?.to}</label>
                <input
                  type="date"
                  value={logDateTo}
                  onChange={e => setLogDateTo(e?.target?.value)}
                  className="border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>
              {/* Room Number */}
              <div className="flex flex-col gap-1 min-w-[130px]">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{labels?.roomNumber}</label>
                <div className="relative">
                  <Icon name="DoorOpen" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    value={logRoomFilter}
                    onChange={e => setLogRoomFilter(e?.target?.value)}
                    placeholder={labels?.searchRoom}
                    className="border border-border rounded-lg pl-8 pr-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-blue-200 w-full"
                  />
                </div>
              </div>
              {/* Guest Name */}
              <div className="flex flex-col gap-1 min-w-[180px]">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{labels?.guestName}</label>
                <div className="relative">
                  <Icon name="Search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    value={logGuestFilter}
                    onChange={e => setLogGuestFilter(e?.target?.value)}
                    placeholder={labels?.searchGuest}
                    className="border border-border rounded-lg pl-8 pr-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-blue-200 w-full"
                  />
                </div>
              </div>
              {/* Department / Building */}
              <div className="flex flex-col gap-1 min-w-[140px]">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{labels?.department}</label>
                <select
                  value={logDeptFilter}
                  onChange={e => setLogDeptFilter(e?.target?.value)}
                  className="border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-blue-200"
                >
                  <option value="All">{labels?.allDepts}</option>
                  {BUILDINGS?.filter(b => b !== 'All')?.map(b => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Summary Statistics */}
          <div className="px-5 py-4 border-b border-border">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
              {language === 'fr' ? 'Statistiques de la période sélectionnée' : 'Summary for Selected Period'}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {[
                { label: labels?.totalResidents, value: logStats?.totalResidents, icon: 'Users', color: '#1B2D5B', bg: '#1B2D5B12' },
                { label: labels?.totalCheckIns, value: logStats?.totalCheckIns, icon: 'LogIn', color: '#22c55e', bg: '#22c55e12' },
                { label: labels?.totalCheckOuts, value: logStats?.totalCheckOuts, icon: 'LogOut', color: '#ef4444', bg: '#ef444412' },
                { label: labels?.avgLengthOfStay, value: logStats?.avgLengthOfStay !== '—' ? `${logStats?.avgLengthOfStay} ${labels?.days}` : '—', icon: 'CalendarDays', color: '#B8963E', bg: '#B8963E12' },
                { label: labels?.periodOccupancyRate, value: `${logStats?.periodOccupancyRate}%`, icon: 'BarChart3', color: '#6366f1', bg: '#6366f112' },
              ]?.map((stat, i) => (
                <div key={i} className="rounded-xl border border-border p-3 flex items-center gap-3" style={{ background: stat?.bg }}>
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold text-white flex-shrink-0" style={{ background: '#1B2D5B' }}>
                    {stat?.icon}
                  </div>
                  <div className="min-w-0">
                    <div className="text-lg font-bold text-foreground leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>{stat?.value}</div>
                    <div className="text-xs text-muted-foreground truncate">{stat?.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Log Table */}
          <div className="overflow-x-auto">
            {filteredLog?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                <Icon name="SearchX" size={40} className="mb-3 opacity-30" />
                <p className="text-sm font-medium">{labels?.noResults}</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/40">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{labels?.resident}</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{labels?.room}</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{labels?.department}</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{labels?.checkIn}</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{labels?.checkOut}</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{labels?.lengthOfStay}</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{labels?.status}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLog?.map((row, i) => {
                    const badge = getStatusBadge(row);
                    return (
                      <tr key={row?.id} className={`border-b border-border/50 hover:bg-muted/30 transition-colors ${i % 2 === 0 ? 'bg-card' : 'bg-muted/10'}`}>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0" style={{ background: '#1B2D5B' }}>
                              {row?.residentName?.charAt(0)?.toUpperCase()}
                            </div>
                            <span className="font-medium text-foreground">{row?.residentName}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3">
                          <span className="font-mono text-xs bg-muted px-2 py-1 rounded text-foreground">{row?.room}</span>
                        </td>
                        <td className="px-5 py-3 text-muted-foreground">{row?.building}</td>
                        <td className="px-5 py-3 text-muted-foreground">{row?.checkIn || '—'}</td>
                        <td className="px-5 py-3 text-muted-foreground">{row?.checkOut || '—'}</td>
                        <td className="px-5 py-3 text-muted-foreground">
                          {row?.lengthOfStay != null ? (
                            <span className="font-medium text-foreground">{row?.lengthOfStay} <span className="text-xs font-normal text-muted-foreground">{labels?.days}</span></span>
                          ) : '—'}
                        </td>
                        <td className="px-5 py-3">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${badge?.cls}`}>
                            <Icon name={badge?.icon} size={11} />
                            {badge?.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default AdvancedReporting;
